export enum ListState {
  Default,
  Opening,
  Open,
  Closing,
  Closed
}

export interface ExtendedHTMLLIElement extends HTMLLIElement {
  getState(): ListState;
  setState(state: ListState): void;
  setTransitionState(targetState: ListState): void;
}

/**
 * Maps ListState enum values to their corresponding data-state strings and vice versa.
 */
function mapState(state?: string | ListState): string | ListState | undefined {
  const stateMapping = {
    opening: ListState.Opening,
    open: ListState.Open,
    closing: ListState.Closing,
    closed: ListState.Closed,
    [ListState.Opening]: 'opening',
    [ListState.Open]: 'open',
    [ListState.Closing]: 'closing',
    [ListState.Closed]: 'closed'
  };

  return stateMapping[state as keyof typeof stateMapping];
}

/**
 * Sets the data-state property and modifies the element in some other ways depending on the state.
 * @param this
 * @param state
 */
export function setState(this: HTMLLIElement, state: ListState) {
  const stateString = mapState(state) as string | undefined;
  if (stateString) {
    this.setAttribute('data-state', stateString);
  }
}
(HTMLLIElement.prototype as any).setState = setState;

/**
 * Returns the data-state property.
 * @param this
 */
export function getState(this: HTMLLIElement): ListState {
  const stateString = this.getAttribute('data-state');
  const state = mapState(stateString) as ListState;

  return state ?? ListState.Default;
}
(HTMLLIElement.prototype as any).getState = getState;

/**
 * Sets the transition state with intermediate states and animations.
 * @param parentElement
 * @param targetState The state to transition to (open or closed).
 */
export function setTransitionState(
  this: HTMLLIElement,
  targetState: ListState
) {
  const childLists = this.querySelectorAll('ul, ol') as NodeListOf<Element>;

  childLists.forEach(() => {
    if (targetState === ListState.Closed) {
      // Start closing transition
      this.setAttribute('data-state', 'open');
    } else if (targetState === ListState.Open) {
      // Start opening transition
      this.setAttribute('data-state', 'closed');
    }
  });
}
(HTMLLIElement.prototype as any).setTransitionState = setTransitionState;
